// Settings → Pipeline steps: the process engine as data, as a real table per stage (Spencer, 2026-10-22; Iris's drawing
// docs/screens/steps-table-screens.html; docs/steps-table-and-instructions-brief.md). Seats at the top (who holds each
// today); one table per stage, folded with counts, the moves-on rule on the open stage's header; every value a picker in
// its cell, the name typed in place, saved on change with Undo on the row; Open shows the step's detail below its row:
// done-when with its event, applies-when with its condition, the description and note, the review link, the answer it
// records, the tick words and second answer, Instructions and Learned doing it, who changed what and when, Retire. The
// level is read, never typed: the database works it out from seat and done-when. Retire, never delete: Retire asks which
// step takes its place, so a lead parked on it comes back there.
//
// Step changes through an AI employee's login (docs/step-changes-brief.md, 2026-10-24; the drawing's section 5): a change
// Alex filed shows in place. Waiting: a ghost row for an add (the rows after it renumber and show "was n"), the proposed
// value under the current one for a change, "Retire → replacement" beside a name, a stage setting beside the current one in
// the stage header, and the Suggested column at the far right (kind, reason, Approve naming what it writes, Change, Send
// back; stale: only Send back). Landed directly: who, when and Undo on the row, the old value small under the new one. The
// stage header counts what waits; on the phone the pending changes are cards above the stages.

import { useCallback, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { changedColumns, stepColumnLabel, stepKeyFrom, stepValueWords, type PipelineStep, type StepChangeRow } from '@zerogap/engine';
import { insertRow, updateRow, type Row } from '../lib/api';
import { usePipeline, type Pipeline } from '../lib/pipeline';
import { useSession } from '../lib/session';
import { Btn, Edit, Fold, Info, Loading, useFolds } from '../ui/kit';
import { nextSort, useDragRows } from '../ui/move';
import { ChangesField, Field, InstructionsField, LearnedField, Picker, Ro, StepTable, useStepFromAddress, useUndo, type Col, type PickOption } from './steps/StepTable';
import { DirectNote, ghostLevel, PendingCards, Prop, StageSuggest, SuggestCell, SuggestChip, useSuggest, Was, type CardLine, type SuggestState } from './steps/changes';

const RULES: { key: PipelineStep['done_rule']; label: string }[] = [
  { key: 'seat_ticks', label: 'The seat ticks it' },
  { key: 'owner_approves', label: 'Owner approves' },
  { key: 'event', label: 'Something happens in ZeroGap' },
  { key: 'client_acts', label: 'The client acts' },
];

/** A row of the table: a step, or a proposed step not yet approved (a ghost, drawn at its proposed position). */
type TableRow = PipelineStep & { ghost?: StepChangeRow };

/** The level words and the i-dot text, from the step_levels and step_table_info settings. */
export function useStepWords() {
  const s = useSession();
  const levels = s.setting<Record<string, string>>('step_levels', {});
  const info = s.setting<Record<string, string>>('step_table_info', {});
  return { levels, info };
}

export function LevelLegend() {
  const { levels, info } = useStepWords();
  return (
    <div className="slv">
      {['L0', 'L1', 'L2', 'L3'].map((l) => (
        <span key={l}>
          <b>{l}</b>
          {levels[l] ?? ''}
          {info[l] && <Info>{info[l]}</Info>}
        </span>
      ))}
    </div>
  );
}

/** Seats · who holds each today: a holder picked in place. */
export function SeatsStrip({ p, canEdit, note }: { p: Pipeline; canEdit: boolean; note?: boolean }) {
  const holders: PickOption[] = [{ key: '', label: 'nobody' }, ...p.users.filter((u) => u.active && !u.is_script).map((u) => ({ key: u.id as string, label: u.name as string, sub: u.is_ai ? 'AI' : undefined }))];
  return (
    <div className="sseats">
      <h4>Seats · who holds each today</h4>
      <div className="sseatrow">
        {p.seats.map((seat) => {
          const holder = p.users.find((u) => u.id === seat.holder_user_id);
          const isOwner = seat.key === 'owner';
          return (
            <div key={seat.id} className="sseat">
              <b>{seat.name}</b>
              {seat.key === 'client' ? (
                <small>the lead's primary contact</small>
              ) : (
                <Picker
                  label={`Who holds ${seat.name}`}
                  value={holder?.name ?? 'nobody'}
                  current={(seat.holder_user_id as string | null) ?? ''}
                  disabled={!canEdit}
                  options={isOwner ? holders.filter((h) => !h.sub) : holders}
                  onPick={(k) => void updateRow('pipeline_seat', seat.id, { holder_user_id: k || null })}
                />
              )}
            </div>
          );
        })}
        {note && (
          <span className="snote">
            A seat says whose step it is. What a login may do is on <Link to="/settings?view=users">Users</Link>; Atlas has Pipeline-manager permissions there without holding the seat.
          </span>
        )}
      </div>
    </div>
  );
}

export function PipelineSteps() {
  const p = usePipeline();
  const s = useSession();
  const folds = useFolds('pipeline-steps');
  const [showRetired, setShowRetired] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const undo = useUndo();
  const canEdit = s.canEdit('screen.settings') || s.canEdit('change.pipeline_process');
  const openStep = useCallback(
    (id: string) => {
      setOpen(id);
      const st = p.steps.find((x) => x.id === id);
      if (st) {
        if (st.retired_at) setShowRetired(true);
        if (!folds.isOpen(st.stage_id)) folds.toggle(st.stage_id);
      }
    },
    [p.steps, folds],
  );
  useStepFromAddress(p.loading ? [] : p.steps, openStep);
  // a change's link (?change=<id>) unfolds its stage and lights the row (an add: the ghost row; a stage setting: the header)
  const openChange = useCallback(
    (c: StepChangeRow) => {
      const stage = c.stage_id ?? (c.target_table === 'pipeline_stage' ? c.target_id : null);
      if (stage && !folds.isOpen(stage)) folds.toggle(stage);
      if (c.target_id && c.target_table === 'pipeline_step' && p.steps.find((x) => x.id === c.target_id)?.retired_at) setShowRetired(true);
    },
    [folds, p.steps],
  );
  const sug = useSuggest('pipeline', p, [], openChange);
  if (p.loading) return <Loading what="Loading the pipeline steps" />;
  const live = p.steps.filter((x) => !x.retired_at);
  const retired = p.steps.filter((x) => !!x.retired_at);
  const stages = [...p.working, ...(p.wonStage ? [p.wonStage] : [])];
  const seatName = (id: unknown) => p.seats.find((x) => x.id === id)?.name ?? '—';
  const words = (c: StepChangeRow, col: string, v: unknown) => stepValueWords(c.target_table, col, v, sug.ctx);
  // the phone's cards restate what Approve writes: step, position, seat, done-when (row PS4)
  const cardLines = (c: StepChangeRow): CardLine[] => {
    if (c.kind === 'add') {
      const liveHere = live.filter((x) => x.stage_id === c.stage_id).sort((a, b) => a.sort - b.sort);
      const pos = Number(sug.proposed(c, 'position') ?? liveHere.length + 1);
      const after = pos <= 1 ? 'first in the stage' : liveHere[pos - 2]?.name ?? 'last in the stage';
      return [
        { k: 'Step', v: <b>{String(sug.proposed(c, 'name') ?? '')}</b> },
        { k: 'After', v: after },
        { k: 'Seat', v: seatName(sug.proposed(c, 'seat_id')) },
        { k: 'Done when', v: sug.proposed(c, 'done_rule') === 'event' ? sug.ctx.eventLabel(String(sug.proposed(c, 'event_key'))) ?? String(sug.proposed(c, 'event_key')) : RULES.find((r) => r.key === sug.proposed(c, 'done_rule'))?.label ?? '—' },
        ...(sug.proposed(c, 'applies_when') ? [{ k: 'Applies when', v: sug.ctx.conditionLabel(String(sug.proposed(c, 'applies_when'))) ?? String(sug.proposed(c, 'applies_when')) }] : []),
        ...(sug.proposed(c, 'gate') ? [{ k: 'Gate', v: 'Yes' }] : []),
        ...(sug.proposed(c, 'back_to_step_id') ? [{ k: 'Back to', v: p.stepById(String(sug.proposed(c, 'back_to_step_id')))?.name ?? '—' }] : []),
      ];
    }
    if (c.kind === 'retire') return [{ k: 'Step', v: <b>{sug.ctx.stepName('pipeline_step', c.target_id)}</b> }, { k: 'Replaced by', v: p.stepById(String(sug.proposed(c, 'replaced_by_step_id')))?.name ?? '—' }, { k: 'Leads moved', v: String(c.target_id ? sug.ctx.leadsOn(c.target_id) : 0) }];
    if (c.kind === 'instructions') return [{ k: 'Step', v: <b>{sug.ctx.stepName('pipeline_step', c.target_id)}</b> }, { k: 'Instructions', v: String(sug.proposed(c, 'instructions') ?? '') }];
    const now = sug.nowOf(c) ?? {};
    return [
      { k: c.kind === 'stage_setting' ? 'Stage' : 'Step', v: <b>{c.kind === 'stage_setting' ? sug.ctx.stageName(c.target_id) : sug.ctx.stepName('pipeline_step', c.target_id)}</b> },
      ...changedColumns(c).map((k) => ({ k: stepColumnLabel(c.target_table, k), v: <>{words(c, k, sug.proposed(c, k))}<small className="swas">was: {words(c, k, now[k])}</small></> })),
    ];
  };
  const directLines = (c: StepChangeRow): CardLine[] => {
    const stage = sug.ctx.stageName(c.stage_id ?? (c.target_table === 'pipeline_stage' ? c.target_id : null));
    const name = c.target_table === 'pipeline_stage' ? stage : sug.ctx.stepName('pipeline_step', c.target_id);
    const v = c.applied ?? c.proposed;
    if (c.kind === 'add') return [{ k: stage ?? '', v: <b>{name}</b> }, { k: 'Added', v: `as step ${String(v.position ?? '?')}` }];
    if (c.kind === 'retire') return [{ k: stage ?? '', v: <b>{name}</b> }, { k: 'Retired', v: `replaced by ${p.stepById(String(v.replaced_by_step_id))?.name ?? '—'}` }];
    if (c.kind === 'instructions') return [{ k: stage ?? '', v: <b>{name}</b> }, { k: 'Instructions', v: 'rewritten' }];
    return [{ k: stage ?? '', v: <b>{name}</b> }, ...changedColumns(c).map((k) => ({ k: stepColumnLabel(c.target_table, k), v: <>{words(c, k, v[k])}<small className="swas">was: {words(c, k, c.undo?.[k])}</small></> }))];
  };

  // the plan of record (Spencer, 2026-10-28): the Pipeline Process Map, its address and last-updated date one setting row, typed when the map is republished
  const map = s.setting<{ url?: string; updated?: string } | null>('pipeline_map', null);
  return (
    <div className="stack stabs">
      {map?.url && (
        <div className="small pmap">
          Plan of record:{' '}
          <a href={map.url} target="_blank" rel="noreferrer">
            Pipeline Process Map
          </a>
          {map.updated ? ` · updated ${map.updated}` : ''}
        </div>
      )}
      <div className="sph">
        <div>
          <h3>Pipeline steps ({live.length})</h3>
          {/* the subtitle and the seats note went 2026-10-24 (F8); the level legend stays */}
          <div className="ssub">
            <LevelLegend />
          </div>
        </div>
        <label className="check sret">
          <input type="checkbox" className="read" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show retired · {retired.length}
        </label>
      </div>
      <SeatsStrip p={p} canEdit={canEdit} />
      {s.device === 'phone' && <PendingCards s={sug} lines={cardLines} directLines={directLines} />}
      {stages.map((st, i) => {
        const rows = p.steps.filter((x) => x.stage_id === st.id && (showRetired || !x.retired_at)).sort((a, b) => a.sort - b.sort);
        const liveHere = rows.filter((x) => !x.retired_at);
        const owner = liveHere.filter((x) => x.seat_id === p.ownerSeat).length;
        const retiredHere = p.steps.filter((x) => x.stage_id === st.id && x.retired_at).length;
        const next = stages[i + 1];
        const gates = liveHere.filter((x) => x.gate);
        return (
          <StageTable
            key={st.id}
            p={p}
            stage={st}
            rows={rows}
            open={folds.isOpen(st.id)}
            onToggle={() => folds.toggle(st.id)}
            canEdit={canEdit}
            openStep={open}
            setOpenStep={setOpen}
            undo={undo}
            sug={sug}
            count={`${liveHere.length} steps${owner ? ` · ${owner} owner` : ''}${retiredHere ? ` · ${retiredHere} retired${showRetired ? '' : ', hidden'}` : ''}`}
            rule={next ? `Moves to ${next.name} when the last step that applies is done${gates.length ? `, or when ${gates.map((g) => g.name).join(' or ')} is done` : ''}` : 'The last stage: Won'}
          />
        );
      })}
    </div>
  );
}

function StageTable({ p, stage, rows, open, onToggle, canEdit, openStep, setOpenStep, undo, sug, count, rule }: { p: Pipeline; stage: Row; rows: PipelineStep[]; open: boolean; onToggle: () => void; canEdit: boolean; openStep: string | null; setOpenStep: (id: string | null) => void; undo: ReturnType<typeof useUndo>; sug: SuggestState; count: string; rule: string }) {
  const { info, levels } = useStepWords();
  const s = useSession();
  const liveRows = rows.filter((x) => !x.retired_at);
  const drag = useDragRows('pipeline_step', liveRows as unknown as Row[], !canEdit);
  const seatName = (id: unknown) => p.seats.find((x) => x.id === id)?.name ?? '—';
  const events = p.events;
  const condLabel = (k: string | null) => (k ? p.conditions.find((c) => c.key === k)?.label ?? k : 'Every lead');
  const ruleLabel = (k: string) => RULES.find((r) => r.key === k)?.label ?? k;
  // one word for empty on Back to and Review link alike (F10, 2026-10-24)
  const backTo = (st: PipelineStep) => (st.back_to_step_id ? p.stepById(st.back_to_step_id)?.name ?? '—' : '—');

  /** Saves one change and leaves its Undo on the row. */
  const change = (st: PipelineStep, field: string, from: string, to: string, patch: Row, back: Row) =>
    void updateRow('pipeline_step', st.id, patch).then(() => undo.record({ rowId: st.id, field, words: `${field} → ${to}`, was: `${field}: ${from} → ${to}`, undo: () => updateRow('pipeline_step', st.id, back) }));
  const changed = (st: PipelineStep, field: string) => undo.note?.rowId === st.id && undo.note.field === field;

  const seatOptions: PickOption[] = p.seats.map((x) => ({ key: x.id, label: x.name, sub: x.key === 'client' ? "the lead's primary contact" : (p.users.find((u) => u.id === x.holder_user_id)?.name as string | undefined) ?? 'nobody' }));
  const doneOptions: PickOption[] = [
    ...RULES.filter((r) => r.key !== 'event').map((r) => ({ key: r.key, label: r.label })),
    ...events.map((e, i) => ({ key: `event:${e.key}`, label: e.label, head: i === 0 ? 'Something happens in ZeroGap' : undefined })),
  ];
  const doneKey = (st: PipelineStep) => (st.done_rule === 'event' ? `event:${st.event_key}` : st.done_rule);
  const doneWordsOf = (key: string) => (key.startsWith('event:') ? events.find((e) => `event:${e.key}` === key)?.label ?? key.slice(6) : ruleLabel(key));
  const donePatch = (key: string): Row => (key.startsWith('event:') ? { done_rule: 'event', event_key: key.slice(6) } : { done_rule: key, event_key: null });
  const pickDone = (st: PipelineStep, key: string) => {
    change(st, 'Done when', doneWordsOf(doneKey(st)), doneWordsOf(key), donePatch(key), { done_rule: st.done_rule, event_key: st.event_key });
  };

  // ---------- the step changes on this stage ----------
  const ghosts = sug.ghostsIn(stage.id as string);
  const pendingHere = sug.waiting.filter((c) => c.stage_id === stage.id || (c.target_table === 'pipeline_stage' && c.target_id === stage.id));
  const stageSetting = sug.waiting.find((c) => c.kind === 'stage_setting' && c.target_id === stage.id);
  const changeOn = (st: TableRow) => (st.ghost ? undefined : sug.on(st.id, 'change'));
  const retireOn = (st: TableRow) => (st.ghost ? undefined : sug.on(st.id, 'retire'));
  const instrOn = (st: TableRow) => (st.ghost ? undefined : sug.on(st.id, 'instructions'));
  const direct = (st: TableRow) => (st.ghost ? undefined : sug.direct.get(st.id));
  const takesOver = (st: TableRow) => !st.ghost && sug.waiting.some((c) => c.kind === 'retire' && c.stage_id === stage.id && c.proposed.replaced_by_step_id === st.id);
  const phone = s.device === 'phone';
  // a ghost row reads the proposal (as edited so far): the step as it would be
  const ghostRow = (c: StepChangeRow): TableRow => ({
    id: `ghost-${c.id}`,
    key: '',
    stage_id: stage.id as string,
    sort: 0,
    name: String(sug.proposed(c, 'name') ?? ''),
    seat_id: String(sug.proposed(c, 'seat_id') ?? ''),
    level: ghostLevel(p, sug.proposed(c, 'seat_id'), sug.proposed(c, 'done_rule')) as PipelineStep['level'],
    applies_when: (sug.proposed(c, 'applies_when') as string | null) ?? null,
    done_rule: (sug.proposed(c, 'done_rule') as PipelineStep['done_rule']) ?? 'seat_ticks',
    event_key: (sug.proposed(c, 'event_key') as string | null) ?? null,
    review_link: !!sug.proposed(c, 'review_link'),
    gate: !!sug.proposed(c, 'gate'),
    back_to_step_id: (sug.proposed(c, 'back_to_step_id') as string | null) ?? null,
    retired_at: null,
    ghost: c,
  });
  // the rows with each ghost at its proposed position among the live steps
  const tableRows: TableRow[] = [];
  {
    const left = [...ghosts];
    let liveSeen = 0;
    for (const r of rows) {
      if (!r.retired_at) {
        for (let i = left.length - 1; i >= 0; i--) if (Number(left[i]!.proposed.position ?? Infinity) <= liveSeen + 1) tableRows.push(ghostRow(left.splice(i, 1)[0]!));
        liveSeen++;
      }
      tableRows.push(r);
    }
    for (const g of left) tableRows.push(ghostRow(g));
  }
  const num = (st: TableRow): ReactNode => {
    if (st.ghost) return <span className="snew">new</span>;
    if (st.retired_at) return '—';
    const i = liveRows.indexOf(st);
    const before = ghosts.filter((g) => Number(g.proposed.position ?? Infinity) <= i + 1).length;
    return before ? (
      <>
        {i + 1 + before}
        <small className="swas">was {i + 1}</small>
      </>
    ) : (
      String(i + 1)
    );
  };
  /** Several proposed columns edited at once (a done-when is a rule and its event). */
  const setEdits = (c: StepChangeRow, patch: Row) => {
    for (const [k, v] of Object.entries(patch)) sug.setEdit(c, k, v);
  };
  /**
   * One cell: a ghost's proposed value (editable while Spencer is changing the proposal); a live step's picker with the proposed
   * value under it (editing the proposal while he is changing it), or the old value under it after a direct write.
   */
  const cell = (st: TableRow, field: string, cols: string[], opts: { label: string; options: PickOption[]; keyOf: (v: Row) => string; wordsOf: (key: string) => ReactNode; subOf?: (v: Row) => ReactNode; patchOf: (key: string) => Row; muted?: (v: Row) => boolean; onLive: (key: string) => void }) => {
    const liveValues: Row = st as unknown as Row;
    if (st.ghost) {
      const c = st.ghost;
      const v: Row = Object.fromEntries(cols.map((k) => [k, sug.proposed(c, k)]));
      const editing = sug.editing(c) && sug.canDecide && !sug.stale(c);
      return <Picker ghost label={opts.label} value={opts.wordsOf(opts.keyOf(v))} sub={opts.subOf?.(v)} current={opts.keyOf(v)} options={opts.options} disabled={!editing} onPick={(k) => setEdits(c, opts.patchOf(k))} />;
    }
    const c = changeOn(st);
    const proposes = c && cols.some((k) => sug.proposes(c, k) && JSON.stringify(sug.proposed(c, k) ?? null) !== JSON.stringify(liveValues[k] ?? null));
    const d = direct(st);
    const was = d && d.kind === 'change' && cols.some((k) => k in (d.undo ?? {}));
    let under: ReactNode;
    if (c && proposes) {
      const v: Row = Object.fromEntries(cols.map((k) => [k, sug.proposes(c, k) ? sug.proposed(c, k) : liveValues[k]]));
      under = <Prop dim={sug.stale(c)}>{opts.wordsOf(opts.keyOf(v))}</Prop>;
    } else if (was) {
      const v: Row = Object.fromEntries(cols.map((k) => [k, k in (d!.undo ?? {}) ? d!.undo![k] : liveValues[k]]));
      under = <Was>{opts.wordsOf(opts.keyOf(v))}</Was>;
    }
    const editingProposal = !!c && proposes && sug.editing(c) && sug.canDecide;
    return (
      <Picker
        label={opts.label}
        value={opts.wordsOf(opts.keyOf(liveValues))}
        sub={opts.subOf?.(liveValues)}
        current={editingProposal ? opts.keyOf(Object.fromEntries(cols.map((k) => [k, sug.proposed(c!, k)]))) : opts.keyOf(liveValues)}
        options={opts.options}
        disabled={!canEdit}
        muted={opts.muted?.(liveValues)}
        changed={changed(st, field)}
        under={under}
        onPick={(k) => (editingProposal ? setEdits(c!, opts.patchOf(k)) : opts.onLive(k))}
      />
    );
  };

  const cols: Col<TableRow>[] = [
    {
      key: 'seat',
      label: 'Seat',
      info: info.seat,
      width: 150,
      cell: (st) =>
        cell(st, 'Seat', ['seat_id'], {
          label: 'Seat',
          options: seatOptions,
          keyOf: (v) => String(v.seat_id ?? ''),
          wordsOf: (k) => seatName(k),
          subOf: (v) => (p.seats.find((x) => x.id === v.seat_id)?.key === 'client' ? "the lead's primary contact" : (p.users.find((u) => u.id === p.seats.find((x) => x.id === v.seat_id)?.holder_user_id)?.name as string | undefined) ?? 'nobody'),
          patchOf: (k) => ({ seat_id: k }),
          onLive: (k) => change(st, 'Seat', seatName(st.seat_id), seatName(k), { seat_id: k }, { seat_id: st.seat_id }),
        }),
    },
    {
      key: 'lvl',
      label: 'Lvl',
      info: info.level,
      width: 60,
      cell: (st) => <span className="slvl" title={st.level ? `${st.level}: ${levels[st.level] ?? ''}` : 'The client acts'}>{st.ghost ? ghostLevel(p, sug.proposed(st.ghost, 'seat_id'), sug.proposed(st.ghost, 'done_rule')) ?? '—' : st.level ?? '—'}</span>,
    },
    {
      key: 'applies',
      label: 'Applies when',
      info: info.applies_when,
      width: 150,
      cell: (st) =>
        cell(st, 'Applies when', ['applies_when'], {
          label: 'Applies when',
          options: [{ key: '', label: 'Every lead' }, ...p.conditions.map((c) => ({ key: c.key, label: c.label }))],
          keyOf: (v) => String(v.applies_when ?? ''),
          wordsOf: (k) => condLabel(k || null),
          patchOf: (k) => ({ applies_when: k || null }),
          muted: (v) => !v.applies_when,
          onLive: (k) => change(st, 'Applies when', condLabel(st.applies_when), condLabel(k || null), { applies_when: k || null }, { applies_when: st.applies_when }),
        }),
    },
    {
      key: 'done',
      label: 'Done when',
      info: info.done_when,
      width: 214,
      cell: (st) =>
        cell(st, 'Done when', ['done_rule', 'event_key'], {
          label: 'Done when',
          options: doneOptions,
          keyOf: (v) => (v.done_rule === 'event' ? `event:${v.event_key}` : String(v.done_rule ?? '')),
          wordsOf: (k) => (k.startsWith('event:') ? ruleLabel('event') : ruleLabel(k)),
          subOf: (v) => (v.done_rule === 'event' ? events.find((e) => e.key === v.event_key)?.label ?? (v.event_key as string | null) ?? undefined : undefined),
          patchOf: donePatch,
          onLive: (k) => pickDone(st, k),
        }),
    },
    {
      key: 'link',
      label: 'Review link',
      info: info.review_link,
      width: 118,
      cell: (st) =>
        cell(st, 'Review link', ['review_link'], {
          label: 'Review link',
          options: [
            { key: 'no', label: '—', sub: 'no link' },
            { key: 'yes', label: 'Leaves a link' },
          ],
          keyOf: (v) => (v.review_link ? 'yes' : 'no'),
          wordsOf: (k) => (k === 'yes' ? 'Leaves a link' : '—'),
          patchOf: (k) => ({ review_link: k === 'yes' }),
          muted: (v) => !v.review_link,
          onLive: (k) => change(st, 'Review link', st.review_link ? 'a link' : 'none', k === 'yes' ? 'a link' : 'none', { review_link: k === 'yes' }, { review_link: st.review_link }),
        }),
    },
    {
      key: 'gate',
      label: 'Gate',
      info: info.gate,
      width: 66,
      cell: (st) =>
        cell(st, 'Gate', ['gate'], {
          label: 'Gate',
          options: [
            { key: 'no', label: 'No' },
            { key: 'yes', label: 'Yes', sub: 'done moves the stage on' },
          ],
          keyOf: (v) => (v.gate ? 'yes' : 'no'),
          wordsOf: (k) => (k === 'yes' ? 'Yes' : 'No'),
          patchOf: (k) => ({ gate: k === 'yes' }),
          muted: (v) => !v.gate,
          onLive: (k) => change(st, 'Gate', st.gate ? 'Yes' : 'No', k === 'yes' ? 'Yes' : 'No', { gate: k === 'yes' }, { gate: st.gate }),
        }),
    },
    {
      key: 'back',
      label: 'Back to',
      info: info.back_to,
      width: 110,
      cell: (st) =>
        cell(st, 'Back to', ['back_to_step_id'], {
          label: 'Back to',
          options: [{ key: '', label: '—', sub: 'no step to go back to' }, ...liveRows.filter((o) => o.id !== st.id).map((o) => ({ key: o.id, label: o.name }))],
          keyOf: (v) => String(v.back_to_step_id ?? ''),
          wordsOf: (k) => (k ? p.stepById(k)?.name ?? '—' : '—'),
          patchOf: (k) => ({ back_to_step_id: k || null }),
          muted: (v) => !v.back_to_step_id,
          onLive: (k) => change(st, 'Back to', backTo(st), k ? p.stepById(k)?.name ?? '' : '—', { back_to_step_id: k || null }, { back_to_step_id: st.back_to_step_id }),
        }),
    },
  ];

  const add = async () => {
    const name = window.prompt('Name the step (by seat, never by person: "Designer drafts…")');
    if (!name?.trim()) return;
    const seat = p.seats.find((x) => x.key === 'pipeline_owner') ?? p.seats[0];
    const row = await insertRow('pipeline_step', {
      key: stepKeyFrom(name, new Set(p.steps.map((x) => x.key))),
      stage_id: stage.id,
      sort: nextSort(p.steps.filter((x) => x.stage_id === stage.id) as unknown as Row[]),
      name: name.trim(),
      seat_id: seat?.id,
      done_rule: 'seat_ticks',
    });
    setOpenStep(row.id);
  };

  // the Suggested column, only while this stage has something pending (the name column gives up the width)
  const tail = pendingHere.some((c) => c.kind !== 'stage_setting')
    ? {
        label: 'Suggested',
        info: info.suggested,
        width: 240,
        cell: (st: TableRow) => {
          const c = st.ghost ?? changeOn(st) ?? retireOn(st) ?? instrOn(st);
          if (!c) return null;
          return <SuggestCell s={sug} c={c} buttons={!phone} stepChanged={null} />;
        },
      }
    : undefined;
  const replacementOptions = (c: StepChangeRow): PickOption[] => p.steps.filter((x) => !x.retired_at && x.id !== c.target_id).map((o) => ({ key: o.id, label: o.name, sub: p.stageById(o.stage_id)?.name as string | undefined }));

  return (
    <Fold
      open={open}
      onToggle={onToggle}
      label={<span className="sstage">{stage.name}</span>}
      right={
        <>
          <small className="srule">{open ? rule : count}</small>
          <SuggestChip n={pendingHere.length} />
          {stageSetting && !phone && <StageSuggest s={sug} c={stageSetting} current={sug.nowOf(stageSetting) ?? {}} />}
        </>
      }
      className="sstg"
    >
      <StepTable<TableRow>
        cols={cols}
        rows={tableRows}
        num={num}
        retired={(st) => !!st.retired_at}
        ghost={(st) => !!st.ghost}
        lit={(st) => !!sug.lit && (st.ghost?.id === sug.lit || sug.all.find((c) => c.id === sug.lit)?.target_id === st.id)}
        tail={tail}
        name={(st) => {
          if (st.ghost) {
            const c = st.ghost;
            const editing = sug.editing(c) && sug.canDecide && !sug.stale(c);
            return (
              <>
                {editing ? <input className="sname ghost" value={String(sug.proposed(c, 'name') ?? '')} aria-label="The proposed step's name" onChange={(e) => sug.setEdit(c, 'name', e.target.value)} /> : <span className={`sname ghost${sug.stale(c) ? ' sstale' : ''}`}>{String(sug.proposed(c, 'name') ?? '')}</span>}
                <span className="schip">suggested</span>
              </>
            );
          }
          const rc = retireOn(st);
          const cc = changeOn(st);
          const d = direct(st);
          return (
            <>
              <Edit className="sname" value={st.name} disabled={!canEdit} onSave={(v) => (v.trim() && v !== st.name ? change(st, 'Name', `“${st.name}”`, `“${v.trim()}”`, { name: v.trim() }, { name: st.name }) : undefined)} />
              {cc && sug.proposes(cc, 'name') && String(sug.proposed(cc, 'name')) !== st.name && <Prop dim={sug.stale(cc)}>{String(sug.proposed(cc, 'name'))}</Prop>}
              {d && d.kind === 'change' && 'name' in (d.undo ?? {}) && <Was>{String(d.undo!.name ?? '—')}</Was>}
              {rc &&
                (sug.editing(rc) && sug.canDecide && !sug.stale(rc) ? (
                  <span className="sretire">
                    Retire → <Picker label="The step that takes its place" value={p.stepById(String(sug.proposed(rc, 'replaced_by_step_id')))?.name ?? '—'} current={String(sug.proposed(rc, 'replaced_by_step_id') ?? '')} options={replacementOptions(rc)} onPick={(k) => sug.setEdit(rc, 'replaced_by_step_id', k)} />
                  </span>
                ) : (
                  <span className={`sretire${sug.stale(rc) ? ' sstale' : ''}`}>Retire → {p.stepById(String(sug.proposed(rc, 'replaced_by_step_id')))?.name ?? '—'}</span>
                ))}
              {takesOver(st) && <small className="stakes">takes over</small>}
              {st.sets_answer && <small>records {answerWords(p, st.sets_answer)}</small>}
              {st.retired_at && <small>retired · {st.replaced_by_step_id ? `replaced by ${p.stepById(st.replaced_by_step_id)?.name ?? ''}` : 'no replacement named'}</small>}
            </>
          );
        }}
        open={openStep}
        setOpen={setOpenStep}
        detail={(st) => <StepDetail p={p} step={st} canEdit={canEdit} onClose={() => setOpenStep(null)} />}
        undo={undo}
        drag={drag}
        canEdit={canEdit}
        flag={(st) => {
          const d = direct(st);
          return d ? <DirectNote s={sug} c={d} /> : null;
        }}
        foot={
          canEdit ? (
            <div className="saddrow">
              <Btn small onClick={() => void add()}>
                + Add a step
              </Btn>
              <small>lands here, at the end of {stage.name}</small>
            </div>
          ) : null
        }
      />
      {stageSetting && phone && (
        <div className="sstg-sug-phone">
          <StageSuggest s={sug} c={stageSetting} current={sug.nowOf(stageSetting) ?? {}} />
        </div>
      )}
    </Fold>
  );
}

/** A condition's rule in words: what the lead's answers must be (first visit: Free walkthrough; Comparable past jobs exist). */
function whenWords(p: Pipeline, when: Record<string, string | boolean>): string {
  const facts: Record<string, string> = { new_lead: 'the contact is new and the lead never parked', reply_by_passed: 'the reply-by date has passed' };
  return Object.entries(when)
    .map(([k, v]) => {
      if (k === 'first_visit' || k === 'design_lane') return `${k === 'first_visit' ? 'first visit' : 'design lane'}: ${p.answers[k].find((a) => a.key === v)?.label ?? String(v)}`;
      const flag = p.answers.flags.find((f) => f.key === k)?.label ?? facts[k] ?? k;
      return v === false ? `not: ${flag}` : flag;
    })
    .join(' · ');
}

const answerWords = (p: Pipeline, key: string) =>
  key === 'first_visit' ? 'the first visit' : key === 'design_lane' ? 'the design lane' : (p.answers.flags.find((f) => f.key === key)?.label ?? key).toLowerCase();

/**
 * The step's detail (F7 and F11, 2026-10-24): what the row has no room for. Done when, Applies when and Review link are
 * edited in the row's cells only, and the boxes that only said why they were empty (Its event, Its condition) are gone;
 * the Level is read-only, worked out by the database from the seat and the done-when, and says so.
 */
function StepDetail({ p, step, canEdit, onClose }: { p: Pipeline; step: PipelineStep; canEdit: boolean; onClose: () => void }) {
  const { info, levels } = useStepWords();
  const save = (patch: Row) => updateRow('pipeline_step', step.id, patch);
  const [retiring, setRetiring] = useState(false);
  const [replacement, setReplacement] = useState('');
  const liveElsewhere = p.steps.filter((x) => x.id !== step.id && !x.retired_at).sort((a, b) => (p.stageById(a.stage_id)?.sort ?? 0) - (p.stageById(b.stage_id)?.sort ?? 0) || a.sort - b.sort);
  const answers: PickOption[] = [{ key: '', label: 'No' }, { key: 'first_visit', label: 'First visit' }, { key: 'design_lane', label: 'Design lane' }, ...p.answers.flags.map((f) => ({ key: f.key, label: f.label }))];
  const leadsOn = p.open.filter((l) => l.step?.id === step.id).length;
  const words = (col: string, v: unknown): string | null => {
    const label: Record<string, string> = { name: 'Name', seat_id: 'Seat', applies_when: 'Applies when', done_rule: 'Done when', event_key: 'Its event', review_link: 'Review link', gate: 'Gate', back_to_step_id: 'Back to', done_when: 'Description', note: 'Note', sets_answer: 'Records', tick_words: 'Tick words', second_answer: 'Second answer', retired_at: 'Retired', replaced_by_step_id: 'Replaced by', sort: 'Order', stage_id: 'Stage', instructions: 'Instructions', learned: 'Learned' };
    if (v === undefined) return label[col] ?? null;
    if (v === null || v === '') return '—';
    if (col === 'seat_id') return p.seats.find((x) => x.id === v)?.name ?? '?';
    if (col === 'back_to_step_id' || col === 'replaced_by_step_id') return p.stepById(String(v))?.name ?? '?';
    if (col === 'stage_id') return p.stageById(String(v))?.name ?? '?';
    if (col === 'applies_when') return p.conditions.find((c) => c.key === v)?.label ?? String(v);
    if (col === 'done_rule') return RULES.find((r) => r.key === v)?.label ?? String(v);
    if (col === 'event_key') return p.events.find((e) => e.key === v)?.label ?? String(v);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (col === 'retired_at') return 'yes';
    return String(v);
  };
  return (
    <div className="sdg-wrap">
      <div className="sdg">
        <Field label="Description" info="What the step is, in words: shown under the step on a lead." wide>
          <Edit multiline className="sin ta" value={step.done_when ?? ''} disabled={!canEdit} onSave={(v) => save({ done_when: v || null })} />
        </Field>
        <Field label="Level" info={info.level}>
          <Ro>
            {step.level ? `${step.level} · ${levels[step.level] ?? ''}` : 'the client acts'}
            <small className="smeta" style={{ display: 'block' }}>set by the seat and the done-when</small>
          </Ro>
        </Field>
        <ChangesField table="pipeline_step" step={step as unknown as Row} users={p.users} words={words} />
        <InstructionsField table="pipeline_step" step={step as unknown as Row} info={info.instructions} users={p.users} />
        <LearnedField table="pipeline_step" step={step as unknown as Row} info={info.learned} users={p.users} />
        <Field label="Records an answer">
          <Picker label="Records an answer" value={answers.find((a) => a.key === (step.sets_answer ?? ''))?.label ?? step.sets_answer ?? 'No'} current={step.sets_answer ?? ''} options={answers} disabled={!canEdit} onPick={(k) => void save({ sets_answer: k || null })} />
        </Field>
        <Field label="The tick button says">
          <Edit className="sin" value={step.tick_words ?? ''} disabled={!canEdit} placeholder="Done" onSave={(v) => save({ tick_words: v || null })} />
        </Field>
        <Field label="A second answer" info="One more one-tap answer: ticks the step with these words on the log.">
          <Edit className="sin" value={step.second_answer ?? ''} disabled={!canEdit} placeholder="none" onSave={(v) => save({ second_answer: v || null })} />
        </Field>
        <Field label="Note" wide>
          <Edit multiline className="sin ta" value={step.note ?? ''} disabled={!canEdit} onSave={(v) => save({ note: v || null })} />
        </Field>
        {step.retired_at && (
          <Field label="Replaced by" info="Where a lead parked on this step comes back.">
            <Picker
              label="Replaced by"
              value={step.replaced_by_step_id ? p.stepById(step.replaced_by_step_id)?.name ?? '—' : 'none'}
              current={step.replaced_by_step_id ?? ''}
              options={[{ key: '', label: 'none' }, ...liveElsewhere.map((o) => ({ key: o.id, label: o.name, sub: p.stageById(o.stage_id)?.name as string | undefined }))]}
              disabled={!canEdit}
              onPick={(k) => void save({ replaced_by_step_id: k || null })}
            />
          </Field>
        )}
      </div>
      <div className="sdfoot">
        <span>
          Changes save as you make them
          {leadsOn ? ` · ${leadsOn} open lead${leadsOn > 1 ? 's' : ''} here now` : ''} · open leads on this step keep it
        </span>
        <span className="sp" />
        {retiring ? (
          <span className="sinline">
            <select value={replacement} onChange={(e) => setReplacement(e.target.value)} aria-label="The step that takes its place">
              <option value="">Which step takes its place?</option>
              {liveElsewhere.map((o) => (
                <option key={o.id} value={o.id}>
                  {p.stageById(o.stage_id)?.name} · {o.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="sob p"
              disabled={!replacement}
              onClick={() => {
                if (leadsOn > 0 && !window.confirm(`${leadsOn} open lead${leadsOn > 1 ? 's are' : ' is'} on this step. Retire it anyway? They keep it until they move on; a parked lead comes back on the replacement.`)) return;
                void save({ retired_at: new Date().toISOString(), replaced_by_step_id: replacement }).then(onClose);
              }}
            >
              Retire
            </button>
            <button type="button" className="sob" onClick={() => setRetiring(false)}>
              Cancel
            </button>
          </span>
        ) : step.retired_at ? (
          canEdit && (
            <button type="button" className="sob" onClick={() => void save({ retired_at: null, replaced_by_step_id: null })}>
              Restore
            </button>
          )
        ) : (
          canEdit && (
            <button type="button" className="sob dim" onClick={() => setRetiring(true)}>
              Retire this step
            </button>
          )
        )}
        <button type="button" className="sob" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
